import { ROLE_LABEL } from './api/types';
import { SlipPrompt } from './components/SlipPrompt';
import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown, ChevronsLeft, ChevronsRight,
  Activity, CalendarRange, Contact, Boxes, Scale, SlidersHorizontal, ArrowLeftRight, ListChecks, Wrench, Network, FileText, Receipt, Send, ReceiptText, FileMinus, Database, ShoppingCart, Truck, Undo2, ClipboardList, Cog, Factory, GanttChartSquare, Grid3x3, Inbox, LogOut, Menu, Package, PlugZap, ScrollText,
  House, Mail, Timer, Upload, Users, ShieldCheck, PackageCheck, PoundSterling, Plus, Repeat, FileBarChart, CheckCheck, Columns3, FileUp, Replace, Percent, ScanBarcode, List, HeartPulse, Gauge, FolderLock, MessageSquareWarning, Building2, ToggleRight, Handshake, type LucideIcon } from 'lucide-react';
import { AttendanceBar } from './components/AttendanceBar';
import { api } from './api/client';
import { Loading, useBrand, useMeta, Wordmark } from './components/ui';
import { GlobalSearch } from './components/GlobalSearch';
import { JobDrawer } from './components/JobDrawer';
import { LoginPage } from './pages/LoginPage';
import { ENQUIRIES } from './modules/enquiries';

const HomePage = lazy(() => import('./pages/HomePage'));
const PlannerPage = lazy(() => import('./pages/PlannerPage'));
const BacklogPage = lazy(() => import('./pages/BacklogPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const MyWorkPage = lazy(() => import('./pages/MyWorkPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const SkillMatrixPage = lazy(() => import('./pages/SkillMatrixPage'));
const CalendarsPage = lazy(() => import('./pages/CalendarsPage'));
const UnleashedUploadPage = lazy(() => import('./pages/UnleashedUploadPage'));
const TransferPricesPage = lazy(() => import('./pages/TransferPricesPage'));
const ComplaintsPage = lazy(() => import('./pages/ComplaintsPage'));
const ComplaintPage = lazy(() => import('./pages/ComplaintsPage').then((m) => ({ default: m.ComplaintPage })));
const IntegrationPage = lazy(() => import('./pages/IntegrationPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const CompanyBrandPage = lazy(() => import('./pages/CompanyBrandPage'));
const ModulesPage = lazy(() => import('./pages/ModulesPage'));
const TradingPartnersPage = lazy(() => import('./pages/TradingPartnersPage'));
const HealthPage = lazy(() => import('./pages/HealthPage'));
const PlanningDataPage = lazy(() => import('./pages/ImportExportPage'));
const ImportExportEmployeesPage = lazy(() => import('./pages/ImportExportPage').then((m) => ({ default: m.ImportExportEmployeesPage })));
const UnleashedCopyPage = lazy(() => import('./pages/UnleashedCopyPage'));
const ClickUpPage = lazy(() => import('./pages/ClickUpPage'));
const ComplaintsDashboardPage = lazy(() => import('./pages/ComplaintsDashboardPage'));
const FileLibraryPage = lazy(() => import('./modules/NotificationsPage').then((m) => ({ default: m.FileLibraryPage })));
const NotificationsPage = lazy(() => import('./modules/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const SuppliersPage = lazy(() => import('./modules/SuppliersPage').then((m) => ({ default: m.SuppliersPage })));
const SupplierPage = lazy(() => import('./modules/SuppliersPage').then((m) => ({ default: m.SupplierPage })));
const CustomersPage = lazy(() => import('./modules/CustomersPage').then((m) => ({ default: m.CustomersPage })));
const PurchasesPage = lazy(() => import('./modules/PurchasesPage').then((m) => ({ default: m.PurchasesPage })));
const PurchaseOrderPage = lazy(() => import('./modules/PurchasesPage').then((m) => ({ default: m.PurchaseOrderPage })));
const ReceiptPage = lazy(() => import('./modules/PurchasesPage').then((m) => ({ default: m.ReceiptPage })));
const SupplierReturnsPage = lazy(() => import('./modules/PurchasesPage').then((m) => ({ default: m.SupplierReturnsPage })));
const SupplierReturnPage = lazy(() => import('./modules/PurchasesPage').then((m) => ({ default: m.SupplierReturnPage })));
const ProductsListPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.ProductsListPage })));
const ProductPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.ProductPage })));
const StockAdjustmentsPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.StockAdjustmentsPage })));
const StockAdjustmentPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.StockAdjustmentPage })));
const WarehouseTransfersPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.WarehouseTransfersPage })));
const WarehouseTransferPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.WarehouseTransferPage })));
const StockCountsPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.StockCountsPage })));
const StockCountPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.StockCountPage })));
const CreateStockCountPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.CreateStockCountPage })));
const QuickCountPage = lazy(() => import('./modules/InventoryPage').then((m) => ({ default: m.QuickCountPage })));
const AssembliesPage = lazy(() => import('./modules/ProductionPage').then((m) => ({ default: m.AssembliesPage })));
const AssemblyPage = lazy(() => import('./modules/ProductionPage').then((m) => ({ default: m.AssemblyPage })));
const BillsOfMaterialsPage = lazy(() => import('./modules/ProductionPage').then((m) => ({ default: m.BillsOfMaterialsPage })));
const BillOfMaterialsPage = lazy(() => import('./modules/ProductionPage').then((m) => ({ default: m.BillOfMaterialsPage })));
const PurchaseKanbanPage = lazy(() => import('./modules/KanbanPage').then((m) => ({ default: m.PurchaseKanbanPage })));
const AssemblyKanbanPage = lazy(() => import('./modules/KanbanPage').then((m) => ({ default: m.AssemblyKanbanPage })));
const ImportPage = lazy(() => import('./modules/ToolsPage').then((m) => ({ default: m.ImportPage })));
const LotEnquiryPage = lazy(() => import('./modules/Lots').then((m) => ({ default: m.LotEnquiryPage })));
const MarkupPage = lazy(() => import('./modules/ToolsPage').then((m) => ({ default: m.MarkupPage })));
const EnquiryPage = lazy(() => import('./modules/EnquiriesPage').then((m) => ({ default: m.EnquiryPage })));
const MarginReportPage = lazy(() => import('./modules/EnquiriesPage').then((m) => ({ default: m.MarginReportPage })));
const PrintManyPage = lazy(() => import('./modules/Bulk').then((m) => ({ default: m.PrintManyPage })));
const ReorderReportPage = lazy(() => import('./modules/ReportsPage').then((m) => ({ default: m.ReorderReportPage })));
const StockCheckPage = lazy(() => import('./modules/StockCheckPage').then((m) => ({ default: m.StockCheckPage })));
const QuotesPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.QuotesPage })));
const QuotePage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.QuotePage })));
const SalesOrdersPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.SalesOrdersPage })));
const BatchCompletePage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.BatchCompletePage })));
const SalesOrderPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.SalesOrderPage })));
const InvoicesPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.InvoicesPage })));
const InvoicePage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.InvoicePage })));
const ShipmentsPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.ShipmentsPage })));
const ShipmentPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.ShipmentPage })));
const CreditsPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.CreditsPage })));
const CreditPage = lazy(() => import('./modules/SalesPage').then((m) => ({ default: m.CreditPage })));
const CustomerPage = lazy(() => import('./modules/CustomersPage').then((m) => ({ default: m.CustomerPage })));
const KittingPage = lazy(() => import('./pages/KittingPage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const XeroPage = lazy(() => import('./pages/XeroPage'));
const ForecastPage = lazy(() => import('./pages/ForecastPage'));
const HrAbsencePage = lazy(() => import('./pages/HrAbsencePage'));
const HrRtwPage = lazy(() => import('./pages/HrRtwPage'));
const HrFilesPage = lazy(() => import('./pages/HrFilesPage'));
const EmployeeFilePage = lazy(() => import('./pages/EmployeeFilePage'));
const EmployeePage = lazy(() => import('./pages/EmployeePage'));
const PerformancePage = lazy(() => import('./pages/PerformancePage'));
const PnlPage = lazy(() => import('./pages/PnlPage'));
const ShippingPage = lazy(() => import('./pages/ShippingPage'));
const ShippingSettingsPage = lazy(() => import('./pages/ShippingSettingsPage'));
const SwitchOverPage = lazy(() => import('./pages/SwitchOverPage'));
const XeroPostingsPage = lazy(() => import('./pages/XeroPostingsPage'));
const PrintPage = lazy(() => import('./modules/PrintPage').then((m) => ({ default: m.PrintPage })));
const CombinedPicklistPage = lazy(() => import('./modules/PrintPage').then((m) => ({ default: m.CombinedPicklistPage })));
const DocumentDetailsPage = lazy(() => import('./modules/PrintPage').then((m) => ({ default: m.DocumentDetailsPage })));
const StockOnHandEnquiryPage = lazy(() => import('./modules/EnquiriesPage').then((m) => ({ default: m.StockOnHandEnquiryPage })));
const TransactionEnquiryPage = lazy(() => import('./modules/EnquiriesPage').then((m) => ({ default: m.TransactionEnquiryPage })));
const SystemListsPage = lazy(() => import('./modules/SystemListsPage').then((m) => ({ default: m.SystemListsPage })));
const DocTemplatesPage = lazy(() => import('./modules/DocTemplatesPage').then((m) => ({ default: m.DocTemplatesPage })));

// ---------- Job drawer context: any screen can open a job / operation ----------
interface DrawerState { jobId: string; operationId?: string }
const DrawerCtx = createContext<{ open: (s: DrawerState) => void; close: () => void; state: DrawerState | null }>({ open: () => {}, close: () => {}, state: null });
export const useJobDrawer = () => useContext(DrawerCtx);

/** The staging install (INSTALL_ROLE=staging): a red bar on every screen, the sign-in page included. */
function StagingBar() {
  const staging = !!useBrand().data?.staging;
  useEffect(() => { document.documentElement.classList.toggle('staging', staging); }, [staging]);
  if (!staging) return null;
  return <div className="staging-bar" role="note" data-testid="staging-bar"><strong>Staging</strong><span className="staging-detail">: a test install. Nothing is sent to email, Xero, Unleashed, UPS or trading partners.</span></div>;
}

export function App() {
  return <><StagingBar /><AppBody /></>;
}

function AppBody() {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<{ user: unknown }>('/api/auth/me'), retry: false, staleTime: 60_000 });
  const qc = useQueryClient();
  useEffect(() => {
    const h = () => { qc.setQueryData(['me'], null); qc.invalidateQueries({ queryKey: ['me'] }); };
    window.addEventListener('mrp:unauthenticated', h);
    return () => window.removeEventListener('mrp:unauthenticated', h);
  }, [qc]);
  if (me.isLoading) return <Loading />;
  if (!me.data) return <LoginPage />;
  return <Shell />;
}

/** Administration links and the area each needs. */
const ADMIN_LINKS: [string, string, LucideIcon, string][] = [
  ['settings.change', '/company', Building2, 'Company and brand'],
  ['settings.change', '/modules', ToggleRight, 'Modules'],
  ['replacement_setup.view', '/document-details', FileText, 'Document details'],
  ['replacement_setup.view', '/document-templates', FileText, 'Document templates'],
  ['replacement_setup.view', '/file-library', FileText, 'File Library'],
  ['replacement_setup.view', '/notifications', Mail, 'Notifications'],
  ['replacement_setup.view', '/system-lists', List, 'System lists'],
  ['roles.change', '/roles', ShieldCheck, 'Roles & permissions'],
  ['clear_planning.change', '/import-export', Upload, 'Planning data'],
  ['settings.change', '/settings', Cog, 'Settings'],
  ['health.view', '/health', Activity, 'System health'],
];
/** Administrators only. The last column: hidden once the planner is the master system. */
const INTEGRATION_LINKS: [string, string, LucideIcon, string, boolean][] = [
  ['unleashed_sync.view', '/integration', PlugZap, 'Unleashed sync', true],
  ['unleashed_upload.change', '/products/unleashed', Upload, 'Upload to Unleashed', true],
  ['unleashed_copy.view', '/unleashed-copy', Database, 'Unleashed copy', false],
  ['unleashed_copy.view', '/stock-check', Scale, 'Stock Check', true],
  ['switch_over.view', '/switch-over', Repeat, 'Switch-over', false],
  ['transfer_prices.view', '/transfer-prices', PoundSterling, 'Transfer prices', false],
  ['xero_leave.view', '/xero', CalendarRange, 'Xero connection', false],
  ['xero_postings.view', '/xero-postings', ReceiptText, 'Xero postings', false],
  ['clickup.change', '/clickup', FileUp, 'ClickUp import', false],
  ['trading_partners.view', '/trading-partners', Handshake, 'Trading partners', false],
];

function Shell() {
  const meta = useMeta();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  // Icon-only navigation rail, remembered per browser
  const [navCollapsed, setNavCollapsed] = useState(() => { try { return localStorage.getItem('emp.navCollapsed') === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem('emp.navCollapsed', navCollapsed ? '1' : '0'); } catch { /* storage unavailable */ } }, [navCollapsed]);
  const [navTip, setNavTip] = useState<{ label: string; y: number } | null>(null);
  const loc = useLocation();
  useEffect(() => setNavOpen(false), [loc.pathname]);
  const qc = useQueryClient();
  const role = meta.data?.user.role;
  const perms = new Set(meta.data?.perms ?? []);
  const can = (p: string) => perms.has(p);
  const canPlan = can('planning.change');
  const brand = useBrand().data;
  useEffect(() => { if (brand) document.title = brand.appName; }, [brand]);
  const counts = useQuery({ queryKey: ['backlog-counts'], queryFn: () => api.get<{ counts: Record<string, number> }>('/api/backlog'), enabled: can('planning.view'), refetchInterval: 60_000 });
  const alerts = useQuery({ queryKey: ['admin-alerts'], queryFn: () => api.get<{ alerts: { key: string; title: string }[] }>('/api/admin/health/alerts'), enabled: role === 'admin' && can('health.view'), refetchInterval: 60_000, retry: false });
  const switched = !!meta.data?.switched;
  const clocking = !!meta.data?.planning?.shopFloorClocking;
  const integ = useQuery({ queryKey: ['integration-summary'], queryFn: () => api.get<{ health: { status: string; lastSuccessfulSyncAt: string | null; failedCount: number } }>('/api/integrations/unleashed'), enabled: can('unleashed_sync.view') && !switched, refetchInterval: 60_000 });

  if (meta.isLoading || !meta.data) return <Loading />;
  const u = meta.data.user;
  const hrScope = meta.data.hr?.scope ?? 'none';
  const erp = (meta.data as { erp?: { master: string; trial: boolean; writable: boolean } | null }).erp ?? null;
  const logout = async () => { await api.post('/api/auth/logout'); qc.clear(); window.location.href = '/'; };
  const home = role === 'employee' ? '/my-work' : role === 'warehouse' && can('kitting.view') ? '/kitting' : can('home.view') ? '/home' : can('planning.view') ? '/planner' : can('assemblies.view') ? '/assemblies' : '/my-work';
  const hs = integ.data?.health;
  const hsOk = hs?.status === 'connected';
  const hsOff = hs?.status === 'disabled';
  const dotColour = !hs || hsOff ? 'var(--text-4)' : hsOk ? 'var(--c-strong-green)' : 'var(--bad)';
  const integrations = role === 'admin' ? INTEGRATION_LINKS.filter(([perm, , , , retired]) => can(perm) && !(retired && switched)) : [];
  const waiting = counts.data ? counts.data.counts.unscheduledOperations + counts.data.counts.exceptions + (counts.data.counts.timeReview ?? 0) : 0;

  return (
    <DrawerCtx.Provider value={{ open: setDrawer, close: () => setDrawer(null), state: drawer }}>
    <NavTipCtx.Provider value={setNavTip}>
      <div className={`shell ${navCollapsed ? 'nav-collapsed' : ''}`}>
        <header className="topbar">
          <button className="menu-btn" onClick={() => setNavOpen((v) => !v)} aria-label="Menu"><Menu size={20} /></button>
          <div className="brand"><Wordmark /></div>
          {can('planning.view') && <GlobalSearch />}
          <div className="spacer" />
          {erp && (erp.trial || erp.master === 'planner') && (
            <NavLink to="/switch-over" className={`erp-pill ${erp.trial ? 'trial' : 'live'}`} data-testid="erp-pill">{erp.trial ? 'TRIAL' : 'LIVE'}</NavLink>
          )}
          {!!alerts.data?.alerts.length && (
            <NavLink to="/health" className="status-pill issue" title={alerts.data.alerts.map((x) => x.title).join(', ')} data-testid="alerts-pill">{alerts.data.alerts.length} alert{alerts.data.alerts.length === 1 ? '' : 's'}</NavLink>
          )}
          {hs && !switched && (
            <NavLink to="/integration" className={`status-pill ${!hsOk && !hsOff ? 'issue' : ''}`} title={`Unleashed: ${hs.status}${hs.failedCount ? `, ${hs.failedCount} failed` : ''}`}>
              <span className="dot" style={{ background: dotColour }} /><span className="sp-label">{`Unleashed ${hsOk ? 'connected' : hsOff ? 'off' : 'needs attention'}`}</span>
            </NavLink>
          )}
          <div className="user">
            <span className="avatar sm" aria-hidden>{u.displayName.split(/\s+/).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}</span>
            <span className="name"><b>{u.displayName}</b>{role && !(role === 'employee' && !u.resourceId) ? ` · ${meta.data.user.roleName ?? ROLE_LABEL[role]}` : ''}</span>
            <button onClick={logout} title="Sign out" aria-label="Sign out"><LogOut size={16} /></button>
          </div>
        </header>
        {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden />}
        <nav className={`sidebar ${navOpen ? 'open' : ''}`} aria-label="Main" onMouseLeave={() => setNavTip(null)}>
          <div className="nav-top">
            <button className="nav-collapse" onClick={() => { setNavCollapsed((v) => !v); setNavTip(null); }} aria-label={navCollapsed ? 'Expand navigation' : 'Collapse navigation'} title={navCollapsed ? 'Expand navigation' : 'Collapse navigation'} aria-expanded={!navCollapsed} data-testid="nav-collapse">
              {navCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
            </button>
          </div>
          {can('home.view') && <NavItem to="/home" icon={House} label="Home" />}
          {(can('planning.view') || can('kitting.view')) && (
            <NavGroup id="planning" label="Planning" icon={GanttChartSquare} count={waiting} alert={!!counts.data?.counts.exceptions || !!counts.data?.counts.timeReview}>
              {can('planning.view') && <NavItem sub to="/planner" icon={GanttChartSquare} label="Planning board" />}
              {can('planning.view') && <NavItem sub to="/backlog" icon={Inbox} label="Backlog & exceptions" count={waiting} alert={!!counts.data?.counts.exceptions || !!counts.data?.counts.timeReview} />}
              {can('planning.view') && <NavItem sub to="/jobs" icon={ClipboardList} label="Jobs" />}
              {can('kitting.view') && <NavItem sub to="/kitting" icon={PackageCheck} label="Kitting" />}
              {can('my_work.view') && (u.resourceId || canPlan) && role !== 'employee' && (canPlan || clocking) && <NavItem sub to="/my-work" icon={Timer} label="My work" />}
            </NavGroup>
          )}
          {role === 'employee' && can('my_work.view') && u.resourceId && clocking && <NavItem to="/my-work" icon={Timer} label="My work" />}
          {(can('employees.view') || can('absence.view') || can('hr_absence.view') || hrScope !== 'none' || can('import_export.change')) && (
            <NavGroup id="hr" label="HR" icon={HeartPulse}>
              {can('employees.view') && <NavItem sub to="/employees" icon={Users} label="Employees" />}
              {can('employees.view') && !meta.data?.modulesOff?.includes('manufacturing') && <NavItem sub to="/skills" icon={Grid3x3} label="Skills matrix" />}
              {can('absence.view') && <NavItem sub to="/calendars" icon={CalendarRange} label="Calendars & absence" />}
              {hrScope !== 'none' && <NavItem sub to="/hr/files" icon={FolderLock} label="Employee files" />}
              {can('hr_absence.view') && <NavItem sub to="/hr/absence" icon={HeartPulse} label="Sickness absence" />}
              {can('import_export.change') && <NavItem sub to="/hr/import-export" icon={FileUp} label="Import/Export Employees" />}
            </NavGroup>
          )}
          {can('purchases.view') && (
            <NavGroup id="purchases" label="Purchases" icon={ShoppingCart}>
              <NavItem sub to="/purchases" icon={ShoppingCart} label="View Purchases" />
              <NavItem sub to="/purchases/kanban" icon={Columns3} label="Purchase Order Kanban" />
              <NavItem sub to="/supplier-returns" icon={Undo2} label="View Supplier Returns" />
              {can('purchases.change') && can('imports.change') && <NavItem sub to="/import/purchaseOrders" icon={FileUp} label="Import Purchase Orders" />}
            </NavGroup>
          )}
          {can('inventory.view') && (
            <NavGroup id="inventory" label="Inventory" icon={Boxes}>
              {erp?.writable && can('inventory.change') && <NavItem sub to="/inventory/products/new" icon={Plus} label="Add Product" />}
              <NavItem sub to="/inventory/products" icon={Boxes} label="View Products" />
              <NavItem sub to="/stock-adjustments" icon={SlidersHorizontal} label="Stock Adjustments" />
              <NavItem sub to="/warehouse-transfers" icon={ArrowLeftRight} label="Warehouse Transfers" />
              <NavItem sub to="/stock-counts" icon={ListChecks} label="Stock Counts" />
              {erp?.writable && can('inventory.change') && <NavItem sub to="/stock-counts/quick" icon={ListChecks} label="Quick Stock Count" />}
              <NavItem sub to="/lots" icon={ScanBarcode} label="Serial/Batch Enquiry" />
              {can('inventory.change') && <>
                {can('imports.change') && <NavItem sub to="/import/stockRevaluation" icon={FileUp} label="Import Stock Revaluation" />}
                {can('imports.change') && <NavItem sub to="/import/products" icon={FileUp} label="Import/Export Products" />}
                {can('imports.change') && <NavItem sub to="/import/changeCodes" icon={Replace} label="Change Codes" />}
                <NavItem sub to="/markup" icon={Percent} label="Markup Price Update" />
              </>}
            </NavGroup>
          )}
          {(can('assemblies.view') || can('boms.view') || can('forecast.view') || can('performance.view') || can('pnl.view') || can('complaints.view')) && (
            <NavGroup id="production" label="Production" icon={Wrench}>
              {can('assemblies.view') && <NavItem sub to="/assemblies" icon={Wrench} label="View Assemblies" />}
              {can('assemblies.view') && <NavItem sub to="/assemblies/kanban" icon={Columns3} label="Assembly Kanban" />}
              {can('boms.view') && <NavItem sub to="/bills-of-materials" icon={Network} label="View Bills of Materials" />}
              {can('boms.change') && can('imports.change') && <NavItem sub to="/import/billsOfMaterials" icon={FileUp} label="Import/Export Bills of Materials" />}
              {can('assemblies.change') && can('imports.change') && <NavItem sub to="/import/assemblies" icon={FileUp} label="Import/Export Assemblies" />}
              {can('forecast.view') && <NavItem sub to="/forecast" icon={PoundSterling} label="Forecast" />}
              {can('performance.view') && <NavItem sub to="/performance" icon={Gauge} label="Production performance" />}
              {can('complaints.view') && <NavItem sub to="/complaints" icon={MessageSquareWarning} label="Customer complaints" />}
              {can('complaints.view') && <NavItem sub to="/complaints-dashboard" icon={FileBarChart} label="Complaints dashboard" />}
              {can('pnl.view') && <NavItem sub to="/pnl" icon={FileBarChart} label="P&L" />}
            </NavGroup>
          )}
          {can('sales.view') && (
            <NavGroup id="sales" label="Sales" icon={Receipt}>
              <NavItem sub to="/quotes" icon={FileText} label="View Quotes" />
              <NavItem sub to="/sales-orders" icon={Receipt} label="View Sales Orders" />
              {erp?.writable && can('sales.change') && <NavItem sub to="/batch-complete" icon={CheckCheck} label="Batch Complete" />}
              <NavItem sub to="/invoices" icon={ReceiptText} label="View Invoices" />
              <NavItem sub to="/shipments" icon={Send} label="View Shipments" />
              <NavItem sub to="/credits" icon={FileMinus} label="View Credits" />
              {erp?.writable && can('sales.change') && <NavItem sub to="/credits/new" icon={Plus} label="Add Credit" />}
              {can('sales.change') && can('imports.change') && <NavItem sub to="/import/salesOrders" icon={FileUp} label="Import Sales Orders" />}
              {can('sales.change') && can('imports.change') && <NavItem sub to="/import/salesQuotes" icon={FileUp} label="Import Quotes" />}
            </NavGroup>
          )}
          {(can('shipping.view') || can('shipping_setup.change')) && (
            <NavGroup id="shipping" label="Shipping" icon={Truck}>
              {can('shipping.view') && <NavItem sub to="/shipping" icon={Truck} label="Ship and track" />}
              {can('shipping_setup.change') && <NavItem sub to="/shipping/settings" icon={Cog} label="Shipping settings" />}
            </NavGroup>
          )}
          {can('customers.view') && (
            <NavGroup id="customers" label="Customers" icon={Contact}>
              {erp?.writable && can('customers.change') && <NavItem sub to="/customers/new" icon={Plus} label="Add Customer" />}
              <NavItem sub to="/customers" icon={Contact} label="View Customers" />
              {can('customers.change') && can('imports.change') && <NavItem sub to="/import/customers" icon={FileUp} label="Import/Export Customers" />}
              {can('customers.change') && can('imports.change') && <NavItem sub to="/import/changeCustomerCodes" icon={Replace} label="Change Codes" />}
            </NavGroup>
          )}
          {can('suppliers.view') && (
            <NavGroup id="suppliers" label="Suppliers" icon={Truck}>
              {erp?.writable && can('suppliers.change') && <NavItem sub to="/suppliers/new" icon={Plus} label="Add Supplier" />}
              <NavItem sub to="/suppliers" icon={Truck} label="View Suppliers" />
              {can('suppliers.change') && can('imports.change') && <NavItem sub to="/import/suppliers" icon={FileUp} label="Import/Export Suppliers" />}
              {can('suppliers.change') && can('imports.change') && <NavItem sub to="/import/changeSupplierCodes" icon={Replace} label="Change Codes" />}
            </NavGroup>
          )}
          {(can('reports.view') || can('audit.view')) && (
            <NavGroup id="reports" label="Reports" icon={FileBarChart}>
              {can('reports.view') && <>
              {ENQUIRIES.filter((x) => x.group === 'Sales').map((x) => <NavItem key={x.path} sub to={`/reports/${x.path}`} icon={FileBarChart} label={x.title} />)}
              {can('margin_report.view') && <NavItem sub to="/reports/customer-product-margin" icon={FileBarChart} label="Margin by Customer and Product" />}
              <NavItem sub to="/reports/reorder" icon={FileBarChart} label="Reorder Report" />
              {ENQUIRIES.filter((x) => x.group === 'Purchase').map((x) => <NavItem key={x.path} sub to={`/reports/${x.path}`} icon={FileBarChart} label={x.title} />)}
              <NavItem sub to="/reports/stock-on-hand" icon={FileBarChart} label="Stock On Hand Enquiry" />
              <NavItem sub to="/reports/transactions" icon={FileBarChart} label="Transaction Enquiry" />
              {ENQUIRIES.filter((x) => x.group === 'Inventory' || x.group === 'Production').map((x) => <NavItem key={x.path} sub to={`/reports/${x.path}`} icon={FileBarChart} label={x.title} />)}
              </>}
              {can('audit.view') && <NavItem sub to="/audit" icon={ScrollText} label="Audit trail" />}
            </NavGroup>
          )}
          {(ADMIN_LINKS.some(([perm]) => can(perm)) || integrations.length > 0) && (
            <NavGroup id="admin" label="Administration" icon={Cog}>
              {ADMIN_LINKS.filter(([perm]) => can(perm)).map(([, to, icon, label]) => <NavItem key={to} sub to={to} icon={icon} label={label} />)}
              {integrations.length > 0 && (
                <NavGroup id="integrations" label="Integrations" icon={PlugZap} nested>
                  {integrations.map(([, to, icon, label]) => <NavItem key={to} sub to={to} icon={icon} label={label} />)}
                </NavGroup>
              )}
            </NavGroup>
          )}
          <div className="version"><Factory size={12} /> <span className="nav-label">{brand?.appName}</span></div>
        </nav>
        {navCollapsed && navTip && <div className="nav-tip" style={{ top: navTip.y }} role="tooltip">{navTip.label}</div>}
        <SlipPrompt />
        <main className="main">
          {hrScope !== 'none' && <AttendanceBar />}
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Navigate to={home} replace />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/planner" element={<PlannerPage />} />
              <Route path="/backlog" element={<BacklogPage />} />
              <Route path="/jobs" element={<JobsPage />} />
              <Route path="/kitting" element={<KittingPage />} />
              <Route path="/my-work" element={<MyWorkPage />} />
              <Route path="/employees" element={<EmployeesPage />} />
              <Route path="/employees/:id" element={<EmployeePage />} />
              <Route path="/skills" element={<SkillMatrixPage />} />
              <Route path="/calendars" element={<CalendarsPage />} />
              <Route path="/products" element={<Navigate to="/inventory/products" replace />} />
              <Route path="/products/unleashed" element={<UnleashedUploadPage />} />
              <Route path="/transfer-prices" element={<TransferPricesPage />} />
              <Route path="/complaints" element={<ComplaintsPage />} />
              <Route path="/complaints-dashboard" element={<ComplaintsDashboardPage />} />
              <Route path="/complaints/:id" element={<ComplaintPage />} />
              <Route path="/products/sub-groups" element={<Navigate to="/products/unleashed" replace />} />
              <Route path="/integration" element={<IntegrationPage />} />
              <Route path="/clickup" element={<ClickUpPage />} />
              <Route path="/file-library" element={<FileLibraryPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/users" element={<Navigate to="/employees" replace />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/company" element={<CompanyBrandPage />} />
              <Route path="/modules" element={<ModulesPage />} />
              <Route path="/trading-partners" element={<TradingPartnersPage />} />
              <Route path="/health" element={<HealthPage />} />
              <Route path="/import-export" element={<PlanningDataPage />} />
              <Route path="/hr/import-export" element={<ImportExportEmployeesPage />} />
              <Route path="/unleashed-copy" element={<UnleashedCopyPage />} />
              <Route path="/switch-over" element={<SwitchOverPage />} />
              <Route path="/xero-postings" element={<XeroPostingsPage />} />
              <Route path="/document-details" element={<DocumentDetailsPage />} />
              <Route path="/document-templates" element={<DocTemplatesPage />} />
              <Route path="/system-lists" element={<SystemListsPage />} />
              <Route path="/system-lists/:key" element={<SystemListsPage />} />
              <Route path="/print/:entity/:guid" element={<PrintPage />} />
              <Route path="/print-combined/picklist" element={<CombinedPicklistPage />} />
              <Route path="/print-many/:entity" element={<PrintManyPage />} />
              <Route path="/purchases" element={<PurchasesPage />} />
              <Route path="/purchases/kanban" element={<PurchaseKanbanPage />} />
              <Route path="/purchases/:guid" element={<PurchaseOrderPage />} />
              <Route path="/purchases/:guid/receipt" element={<ReceiptPage />} />
              <Route path="/supplier-returns" element={<SupplierReturnsPage />} />
              <Route path="/supplier-returns/:guid" element={<SupplierReturnPage />} />
              <Route path="/inventory/products" element={<ProductsListPage />} />
              <Route path="/inventory/products/:guid" element={<ProductPage />} />
              <Route path="/stock-adjustments" element={<StockAdjustmentsPage />} />
              <Route path="/stock-adjustments/:guid" element={<StockAdjustmentPage />} />
              <Route path="/warehouse-transfers" element={<WarehouseTransfersPage />} />
              <Route path="/warehouse-transfers/:guid" element={<WarehouseTransferPage />} />
              <Route path="/stock-counts" element={<StockCountsPage />} />
              <Route path="/stock-counts/new" element={<CreateStockCountPage />} />
              <Route path="/stock-counts/quick" element={<QuickCountPage />} />
              <Route path="/stock-counts/:guid" element={<StockCountPage />} />
              <Route path="/stock-check" element={<StockCheckPage />} />
              <Route path="/reports/reorder" element={<ReorderReportPage />} />
              <Route path="/reports/customer-product-margin" element={<MarginReportPage />} />
              <Route path="/reports/stock-on-hand" element={<StockOnHandEnquiryPage />} />
              <Route path="/reports/transactions" element={<TransactionEnquiryPage />} />
              <Route path="/reports/:enquiry" element={<EnquiryPage />} />
              <Route path="/assemblies" element={<AssembliesPage />} />
              <Route path="/assemblies/kanban" element={<AssemblyKanbanPage />} />
              <Route path="/assemblies/:guid" element={<AssemblyPage />} />
              <Route path="/bills-of-materials" element={<BillsOfMaterialsPage />} />
              <Route path="/bills-of-materials/:guid" element={<BillOfMaterialsPage />} />
              <Route path="/quotes" element={<QuotesPage />} />
              <Route path="/quotes/:guid" element={<QuotePage />} />
              <Route path="/sales-orders" element={<SalesOrdersPage />} />
              <Route path="/batch-complete" element={<BatchCompletePage />} />
              <Route path="/import/:kind" element={<ImportPage />} />
              <Route path="/markup" element={<MarkupPage />} />
              <Route path="/lots" element={<LotEnquiryPage />} />
              <Route path="/sales-orders/:guid" element={<SalesOrderPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/invoices/:guid" element={<InvoicePage />} />
              <Route path="/shipments" element={<ShipmentsPage />} />
              <Route path="/shipments/:guid" element={<ShipmentPage />} />
              <Route path="/credits" element={<CreditsPage />} />
              <Route path="/credits/:guid" element={<CreditPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:guid" element={<CustomerPage />} />
              <Route path="/suppliers" element={<SuppliersPage />} />
              <Route path="/suppliers/:guid" element={<SupplierPage />} />
              <Route path="/xero" element={<XeroPage />} />
              <Route path="/forecast" element={<ForecastPage />} />
              <Route path="/hr/absence" element={<HrAbsencePage />} />
              <Route path="/hr/return-to-work" element={<HrRtwPage />} />
              <Route path="/hr/files" element={<HrFilesPage />} />
              <Route path="/hr/files/:id" element={<EmployeeFilePage />} />
              <Route path="/performance" element={<PerformancePage />} />
              <Route path="/pnl" element={<PnlPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/shipping/settings" element={<ShippingSettingsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="*" element={<Navigate to={home} replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
      {drawer && <JobDrawer key={drawer.jobId} jobId={drawer.jobId} operationId={drawer.operationId} onClose={() => setDrawer(null)} />}
    </NavTipCtx.Provider>
    </DrawerCtx.Provider>
  );
}

/* ---------- Navigation ---------- */
const NavTipCtx = createContext<(t: { label: string; y: number } | null) => void>(() => undefined);

/**
 * A menu group as Unleashed lays them out: the group's icon and name, opening to its screens beneath.
 * The group holding the current screen is always open; others remember whether they were opened.
 * In the collapsed rail the group heading is hidden and each screen shows as an icon.
 */
/** A menu group. Every group starts collapsed when the app opens; the group holding the current screen is marked. */
function NavGroup({ id, label, icon: Icon, children, count, alert, nested }: { id: string; label: string; icon: LucideIcon; children: ReactNode; count?: number; alert?: boolean; nested?: boolean }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [holdsCurrent, setHoldsCurrent] = useState(false);
  useEffect(() => { setHoldsCurrent(!!ref.current?.querySelector('a.active')); }, [pathname]);
  return (
    <div className={`nav-group ${nested ? 'nested' : ''} ${open ? 'open' : ''} ${holdsCurrent ? 'current' : ''}`} ref={ref}>
      <button className="nav-group-head" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`nav-group-${id}`}>
        <span className="nav-ico" aria-hidden><Icon size={14} strokeWidth={2} /></span>
        <span className="nav-label">{label}</span>
        {!open && !!count && <span className={`count ${alert ? 'count-warn' : ''}`}>{count}</span>}
        <ChevronDown size={14} className="nav-chevron" aria-hidden />
      </button>
      <div className="nav-group-items">{children}</div>
    </div>
  );
}

/** One destination: a white-on-blue icon block, then the label. In the collapsed rail only the block shows, with a tooltip. */
function NavItem({ to, icon: Icon, label, count, alert, sub }: { to: string; icon: LucideIcon; label: string; count?: number; alert?: boolean; sub?: boolean }): ReactNode {
  const setTip = useContext(NavTipCtx);
  return (
    <NavLink to={to} className={sub ? 'sub' : undefined}
      onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ label: count ? `${label} (${count})` : label, y: r.top + r.height / 2 }); }}
      onFocus={(e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ label, y: r.top + r.height / 2 }); }}
      onBlur={() => setTip(null)}>
      <span className="nav-ico" aria-hidden><Icon size={14} strokeWidth={2} />{!!count && <i className={`nav-dot ${alert ? 'nav-dot-warn' : ''}`} />}</span>
      <span className="nav-label">{label}</span>
      {!!count && <span className={`count ${alert ? 'count-warn' : ''}`}>{count}</span>}
    </NavLink>
  );
}
